import { locationDataLoader } from '../data/LocationDataLoader.js';
import { getGUI } from '../OverworldAnimation.js';
import { getTownMapPosition } from '../data/townMapPositions.js';

export default class Location {
    static get(locationId) {
        // Try to load from API data first
        const locationData = locationDataLoader.getLocationData(locationId);

        if (locationData) {
            return new Location({
                locationId: locationData.identifier,
                name: locationData.name,
                encounterRate: locationData.encounterRate,
                encounterRateRockSmash: locationData.encounterRateRockSmash ?? 1.0,
                encounterRateCut: locationData.encounterRateCut ?? 1.0,
                canBike: locationData.canBike,
                needsFlash: locationData.needsFlash,
                fixedTimeOfDay: locationData.fixedTimeOfDay,
                encounters: locationData.encounters,
                townMapPosition: locationData.townMapPosition ?? getTownMapPosition(locationData.identifier),
            });
        }
        // Fallback to hardcoded unknown location if not found
        return new Location({
            locationId: 'unknown',
            name: 'Unknown',
            availablePokemon: [{ id: 201, chance: 100, minLevel: 5, maxLevel: 5 }], // Unown
            encounterRate: 0.01,
            encounterRateRockSmash: 1.0,
            encounterRateCut: 1.0,
            canBike: true,
            needsFlash: false,
            fixedTimeOfDay: null,
            encounters: null,
            townMapPosition: null,
        });
    }

    constructor({
        locationId,
        name,
        availablePokemon = null, // Legacy support - deprecated, use encounters instead
        encounterRate,
        encounterRateRockSmash = 1.0,
        encounterRateCut = 1.0,
        canBike = true,
        needsFlash = false,
        fixedTimeOfDay = null,
        encounters = null,
        townMapPosition = null,
    }) {
        this.locationId = locationId;
        this.name = name;
        this.encounterRate = encounterRate;
        this.encounterRateRockSmash = encounterRateRockSmash;
        this.encounterRateCut = encounterRateCut;
        this.canBike = canBike;
        this.needsFlash = needsFlash;
        this.fixedTimeOfDay = fixedTimeOfDay;
        this.encounters = encounters;
        this.townMapPosition = townMapPosition;

        // Legacy support: if availablePokemon is provided, use it
        // Otherwise, encounters will be used via getAvailablePokemon
        this.availablePokemon = availablePokemon;
    }

    /**
     * Get all available Pokemon for a specific time of day across all encounter types
     * @param {string} timeOfDay - 'day' | 'dusk' | 'night' | 'dawn'
     * @returns {Array} Array of encounter objects with { pokemonId, chance, minLevel, maxLevel }
     */
    getAvailablePokemon(timeOfDay) {
        // If location has fixedTimeOfDay, use that instead
        const effectiveTimeOfDay = this.fixedTimeOfDay || timeOfDay;
        // If no encounters data, fall back to legacy availablePokemon
        if (
            !this.encounters ||
            !Array.isArray(this.encounters) ||
            !this.encounters.some((encounter) => encounter.timeOfDay === effectiveTimeOfDay)
        ) {
            // Convert legacy format (id) to new format (pokemonId)
            const ap = (this.availablePokemon || []).map((pokemon) => ({
                pokemonId: pokemon.id || pokemon.pokemonId,
                chance: pokemon.chance,
                minLevel: pokemon.minLevel,
                maxLevel: pokemon.maxLevel,
            }));
            return ap;
        }

        const timeOfDayEncounters = this.encounters.filter(
            (encounter) => encounter.timeOfDay === effectiveTimeOfDay
        );
        return timeOfDayEncounters.map((ec) => {
            // Handle both pokemonId field and pokemon.id nested object
            const pokemonId = ec.pokemonId || (ec.pokemon && ec.pokemon.id);
            return {
                pokemonId: pokemonId,
                chance: ec.chance,
                minLevel: ec.minLevel,
                maxLevel: ec.maxLevel,
                encounterType: ec.encounterType,
                encounterItems: ec.encounterItems ?? [],
            };
        });

        // const allEncounters = [];
        // debugger;

        // // Collect encounters from all encounter types (land, water, old_rod, etc.)
        // timeOfDayEncounters.forEach((encounterType) => {
        //     const encounters = timeOfDayEncounters[encounterType];
        //     if (Array.isArray(encounters)) {
        //         encounters.forEach((encounter) => {
        //             allEncounters.push({
        //                 pokemonId: encounter.pokemonId,
        //                 chance: encounter.chance,
        //                 minLevel: encounter.minLevel,
        //                 maxLevel: encounter.maxLevel,
        //                 encounterType: encounterType, // Include encounter type for filtering
        //             });
        //         });
        //     }
        // });

        // return allEncounters;
    }

    getAvailableWaterPokemon() {
        const availablePokemon = this.getAvailablePokemon(getGUI().getTimeOfDay()).filter(
            (ap) => ap.encounterType === 'water'
        );
        return availablePokemon;
    }

    getAvailableLandPokemon() {
        return this.getAvailablePokemon(getGUI().getTimeOfDay()).filter((ap) => ap.encounterType === 'land');
    }

    getAvailableCutPokemon() {
        return this.getAvailablePokemon(getGUI().getTimeOfDay()).filter(
            (ap) => ap.encounterType === 'cut'
        );
    }

    getAvailableRockSmashPokemon() {
        return this.getAvailablePokemon(getGUI().getTimeOfDay()).filter(
            (ap) => ap.encounterType === 'rock_smash'
        );
    }

    /**
     * Get available land Pokemon for a specific time of day
     * @param {string} timeOfDay - 'day' | 'dusk' | 'night' | 'dawn'
     * @returns {Array} Array of land encounter objects with { pokemonId, chance, minLevel, maxLevel }
     */
    // getAvailableLandPokemon() {
    //     // If location has fixedTimeOfDay, use that instead
    //     const effectiveTimeOfDay = this.fixedTimeOfDay || getGUI().getTimeOfDay();

    //     // If no encounters data, fall back to legacy availablePokemon (assumed to be land)
    //     if (
    //         !this.encounters ||
    //         !Array.isArray(this.encounters) ||
    //         !this.encounters.some((encounter) => encounter.timeOfDay === effectiveTimeOfDay)
    //     ) {
    //         // Convert legacy format (id) to new format (pokemonId)
    //         return (this.availablePokemon || []).map((pokemon) => ({
    //             pokemonId: pokemon.id || pokemon.pokemonId,
    //             chance: pokemon.chance,
    //             minLevel: pokemon.minLevel,
    //             maxLevel: pokemon.maxLevel,
    //         }));
    //     }

    //     const timeOfDayEncounters = this.encounters.find(
    //         (encounter) => encounter.timeOfDay === effectiveTimeOfDay
    //     );
    //     const landEncounters = timeOfDayEncounters.land || [];

    //     // Transform to match expected format
    //     return landEncounters.map((encounter) => ({
    //         pokemonId: encounter.pokemonId,
    //         chance: encounter.chance,
    //         minLevel: encounter.minLevel,
    //         maxLevel: encounter.maxLevel,
    //     }));
    // }
}
